import * as React from 'react';
import { SettingsComponentRegistry } from '../../general-settings/components/SettingsComponentRegistry';
import { ComponentSpecs } from '../../general-settings/components/SettingsComponentRegistryFunctions';
import { SectionNameContext } from './SettingsCoordinatorContexts';

export interface SectionContainerBaseProps<SettingsType extends object> {
  sections: (string | ComponentSpecs<{ settings: SettingsType }>)[];
  settings: SettingsType;
  formStyle?: string;  // Added to set the root form styling
}

/**
 * Renders a list of settings sections specified in the `sections` prop. Each list entry is either a {@link ComponentSpecs},
 * or a name of a component spec that is added to the {@link SettingsComponentRegistry}.
 * It also sets up a {@link SectionNameContext} with the section name which can be accessed by any descendant component
 * if needed
 */
export function SettingsSectionList<SettingsType extends object>(props: SectionContainerBaseProps<SettingsType>) {
  const {sections, formStyle, ...rest} = props;
  const unregisteredSections = sections?.filter(key => typeof (key) === 'string' && !(key in SettingsComponentRegistry));

  if (unregisteredSections?.length > 0) {
    throw new Error(`Tried to render unregistered settings sections: ${unregisteredSections}`);
  }

  return (
    <div className={formStyle}>{
      sections?.map((spec, index) => {
        const [Section, key] =
          typeof (spec) === 'string'
          ? [SettingsComponentRegistry[spec], spec]
          : [spec, spec.name];

        return (
          <SectionNameContext.Provider key={index} value={key}>
            <Section.component {...rest}/>
          </SectionNameContext.Provider>
        );
      })
    }
    </div>
  );
}
